import {
  X,
  Info,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";

/* ---------------- TYPES ---------------- */

type BaseProps = {
  title: string;
  message: string;
  onClose?: () => void;
  enabled?: boolean; // 👈 ONLY FLAG
};

/* ---------------- STYLES ---------------- */

const baseStyle = {
  info: {
    border: "border-teal-500",
    icon: Info,
    color: "text-teal-500",
    titleColor: "text-teal-700",
  },
  success: {
    border: "border-[#002B6E]",
    icon: CheckCircle,
    color: "text-[#002B6E]",
    titleColor: "text-[#002B6E]",
  },
  warning: {
    border: "border-orange-500",
    icon: AlertTriangle,
    color: "text-orange-500",
    titleColor: "text-orange-700",
  },
  danger: {
    border: "border-error",
    icon: AlertCircle,
    color: "text-error",
    titleColor: "text-error",
  },
};

/* ---------------- CORE COMPONENT ---------------- */

const AlertCore = ({
  type,
  title,
  message,
  onClose,
  enabled = true,
}: BaseProps & { type: keyof typeof baseStyle }) => {
  if (!enabled) return null; // 👈 HARD STOP

  const config = baseStyle[type];
  const Icon = config.icon;

  return (
    <div
      className={`relative border-l-4 ${config.border} bg-gray-50 p-4 shadow-sm`}
    >
      <div className="flex gap-3">
        <div className="flex items-center gap-2">
          <Icon className={config.color} size={20} />

          <h4 className={`font-semibold ${config.titleColor}`}>
            {title}
          </h4>
        </div>
      </div>

      <p className="mt-2 text-sm text-gray-600">{message}</p>

      <button
        onClick={() => onClose?.()}
        aria-label="Close alert"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
      >
        <X size={18} />
      </button>
    </div>
  );
};

/* ---------------- TYPE-SPECIFIC COMPONENTS ---------------- */

export const InfoAlert = (props: BaseProps) => (
  <AlertCore type="info" {...props} />
);

export const SuccessAlert = (props: BaseProps) => (
  <AlertCore type="success" {...props} />
);

export const WarningAlert = (props: BaseProps) => (
  <AlertCore type="warning" {...props} />
);

export const DangerAlert = (props: BaseProps) => (
  <AlertCore type="danger" {...props} />
);